// Twilio Message History Service
// Fetches actual SMS message history from Supabase SMS table

import { getSupabase } from "../supabaseClient";
import { getBackendURL } from "../env";

// Backend API base URL from env (same as rest of app)
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  return `${getBackendURL()}/api`;
};

const API_BASE_URL = getApiBaseUrl();

// Cache for Twilio configuration
let twilioConfigCache = null;
let configCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Patient preload cache - stored in memory and sessionStorage
const PATIENT_CACHE_KEY = 'twilio_patient_lookup';
const PATIENT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let patientCacheInMemory = null;
let patientCacheTimestamp = null;

// Fetch Twilio configuration from backend
const fetchTwilioConfigFromBackend = async (sync = false, syncAll = false) => {
  try {
    const url = new URL(`${API_BASE_URL}/twilio/config/`);
    if (sync) {
      url.searchParams.append("sync", "true");
    }
    if (syncAll) {
      url.searchParams.append("sync_all", "true");
    }
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching Twilio config from backend:", error);
    throw error;
  }
};

// Get cached or fresh Twilio configuration
const getTwilioConfig = async (sync = false, syncAll = false) => {
  const now = Date.now();

  // Return cached config if it's still valid AND we're not forcing a sync
  if (
    !sync &&
    !syncAll &&
    twilioConfigCache &&
    configCacheTimestamp &&
    now - configCacheTimestamp < CACHE_DURATION
  ) {
    return twilioConfigCache;
  }

  try {
    // Fetch fresh configuration from backend
    const config = await fetchTwilioConfigFromBackend(sync, syncAll);

    // Cache the configuration
    twilioConfigCache = config;
    configCacheTimestamp = now;

    return config;
  } catch (error) {
    console.error("Failed to fetch Twilio configuration:", error);

    // Return cached config if available, otherwise return error state
    if (twilioConfigCache) {
      console.warn("Using cached Twilio configuration due to fetch error");
      return twilioConfigCache;
    }

    // Return error configuration
    return {
      status: "error",
      accountSid: null,
      phoneNumber: null,
      messageCount: 0,
      errorCount: 0,
      successRate: 0,
      recentMessages: [],
      configuration: {
        accountSidConfigured: false,
        authTokenConfigured: false,
        phoneNumberConfigured: false,
      },
      timestamp: new Date().toISOString(),
      error: "Failed to fetch configuration from backend",
    };
  }
};

export const getTwilioConfiguration = async (sync = false, syncAll = false) => {
  try {
    const config = await getTwilioConfig(sync, syncAll);
    return config;
  } catch (error) {
    console.error("Error getting Twilio configuration:", error);
    return {
      status: "error",
      accountSid: null,
      phoneNumber: null,
      messageCount: 0,
      errorCount: 0,
      successRate: 0,
      recentMessages: [],
      configuration: {
        accountSidConfigured: false,
        authTokenConfigured: false,
        phoneNumberConfigured: false,
      },
      timestamp: new Date().toISOString(),
      error: error.message,
    };
  }
};

// Get message count from Twilio without syncing
export const getTwilioMessageCount = async (fullSync = false) => {
  try {
    const url = new URL(`${API_BASE_URL}/twilio/config/`);
    url.searchParams.append("count_only", "true");
    if (fullSync) {
      url.searchParams.append("sync_all", "true");
    }

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.totalMessagesToSync || 0;
  } catch (error) {
    console.error("Error getting Twilio message count:", error);
    return 0;
  }
};

// Get sync progress by sync_id
export const getSyncProgress = async (syncId) => {
  try {
    const url = new URL(`${API_BASE_URL}/twilio/config/`);
    url.searchParams.append("sync_id", syncId);

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting sync progress:", error);
    return null;
  }
};

export const syncTwilioMessages = async (fullSync = false) => {
  return await getTwilioConfiguration(true, fullSync);
};

export const testTwilioConnection = async () => {
  try {
    const config = await getTwilioConfiguration();
    const configComplete = Object.values(config.configuration || {}).every(
      Boolean,
    );

    return {
      success: configComplete,
      message: configComplete
        ? "Twilio connection test successful - all configuration is properly set"
        : "Twilio connection test failed - incomplete configuration detected",
      messageSid: configComplete ? `test-${Date.now()}` : null,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    return {
      success: false,
      message: `Twilio connection test failed: ${error.message}`,
      messageSid: null,
      timestamp: new Date().toISOString(),
    };
  }
};

// Resend a Twilio message
export const resendTwilioMessage = async (
  phoneNumber,
  message,
  split = false,
  scheduleDate = null,
  oldSmsId = null, // ID of the old failed SMS to replace
  appointmentDate = null, // Appointment date to store with the message
  schema = null, // Provider schema the old SMS lives in: sender number, storage and delete all use it
  confirmBadNumber = false, // User chose "Send anyway" in the bad-number pop-up (utils/badNumbers.js)
) => {
  try {
    const body = {
      phone_number: phoneNumber,
      message: message,
      split: split,
    };
    if (schema) {
      body.schema = schema;
    }
    if (scheduleDate) {
      body.schedule_date = scheduleDate;
    }
    if (oldSmsId) {
      body.old_sms_id = oldSmsId;
    }
    if (appointmentDate) {
      body.appointment_date = appointmentDate;
    }
    if (confirmBadNumber) {
      body.confirm_bad_number = true;
    }
    const response = await fetch(`${API_BASE_URL}/twilio/resend/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error resending Twilio message:", error);
    throw error;
  }
};

// Pagination settings
const MESSAGES_PER_PAGE = 10;

// Fetch message history with pagination support
export const fetchMessageHistory = async (
  page = 0,
  limit = MESSAGES_PER_PAGE,
  searchQuery = "",
  getAll = false,
) => {
  try {
    const supabase = await getSupabase();

    // Get Twilio configuration to get the phone number
    const config = await getTwilioConfig();
    const twilioPhoneNumber = config.phoneNumber;

    // Calculate offset for pagination
    const offset = getAll ? 0 : page * limit;

    // Build query
    let query = supabase
      .from("SMS")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false });

    // Apply search filter if provided
    if (searchQuery) {
      query = query.or(
        `message.ilike.%${searchQuery}%,from.ilike.%${searchQuery}%,to.ilike.%${searchQuery}%`,
      );
    }

    // Fetch messages - either paginated or all
    let data, error, count;
    if (getAll) {
      // Fetch all messages without pagination
      const result = await query;
      data = result.data || [];
      count = result.count || 0;
    } else {
      // Fetch messages with pagination
      const result = await query.range(offset, offset + limit - 1);
      data = result.data || [];
      error = result.error;
      count = result.count || 0;
    }

    if (error) {
      console.error("Error fetching message history:", error);
      return { messages: [], totalCount: 0 };
    }

    // Transform messages for display
    const messages = (data || []).map((msg) => {
      // DEBUG: Log direction values for diagnosis
      console.log(`[SMS DEBUG] id=${msg.id}, direction=${JSON.stringify(msg.direction)}, from=${msg.from}, to=${msg.to}, twilioPhone=${twilioPhoneNumber}`);
      
      // Use direction from DB if available, otherwise infer from phone comparison
      // Outbound: from=Twilio number, to=patient
      // Inbound: from=patient, to=Twilio number
      let isOutbound = msg.direction === 'outbound' 
        ? true 
        : msg.direction === 'inbound' 
          ? false 
          : msg.from === twilioPhoneNumber; // Fallback to phone comparison
      
      console.log(`[SMS DEBUG] isOutbound=${isOutbound} (based on direction=${msg.direction})`);

      // Use actual status from DB if available, otherwise infer
      let status = msg.status;
      if (!status) {
        status = isOutbound ? "sent" : "received";
      }

      // Extract phone number for patient lookup - patient phone is 'to' for outbound, 'from' for inbound
      const phoneNumber = isOutbound ? msg.to : msg.from;
      console.log(`[SMS DEBUG] phoneNumber=${phoneNumber} (isOutbound=${isOutbound})`);

      // Try to get patient name from msg if available (stored during message creation)
      // Fall back to extracting from message content if available
      let patientName = msg.patient_name || null;
      
      // If no patient_name stored directly on msg, try to extract from message metadata
      if (!patientName && msg.metadata) {
        try {
          const metadata = typeof msg.metadata === 'string' ? JSON.parse(msg.metadata) : msg.metadata;
          patientName = metadata.patient_name || metadata.name || null;
        } catch (e) {
          // Metadata parse failed, ignore
        }
      }

      return {
        id: msg.id,
        timestamp: msg.created_at,
        // Use the isOutbound computed above (which properly checks msg.direction first)
        direction: isOutbound ? "outbound" : "inbound",
        status: status,
        phoneNumber: phoneNumber,
        preview:
          msg.message.length > 100
            ? `${msg.message.substring(0, 100)}...`
            : msg.message,
        fullMessage: msg.message,
        createdAt: new Date(msg.created_at).toISOString(),
        errorCode: msg.error_code,
        errorMessage: msg.error_message,
        patientName: patientName,
        patientId: msg.patient_id || null,
      };
    });

    return { messages, totalCount: count || 0 };
  } catch (error) {
    console.error("Failed to fetch message history:", error);
    return { messages: [], totalCount: 0 };
  }
};

// Get total message count
export const getTotalMessageCount = async () => {
  try {
    const supabase = await getSupabase();
    const { count, error } = await supabase
      .from("SMS")
      .select("*", { count: "exact", head: true });

    if (error) {
      console.error("Error counting messages:", error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error("Failed to count messages:", error);
    return 0;
  }
};

export const getMessageStatistics = async () => {
  try {
    const supabase = await getSupabase();

    // Get Twilio configuration to get the phone number
    const config = await getTwilioConfig();
    const twilioPhoneNumber = config.phoneNumber;

    // Get messages from last 24 hours
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const { data: recentMessages, error } = await supabase
      .from("SMS")
      .select("*")
      .gte("created_at", yesterday.toISOString());

    if (error) {
      console.error("Error fetching recent messages for statistics:", error);
      return {
        totalCount: 0,
        errorCount: 0,
        successRate: 100,
      };
    }

    const totalCount = recentMessages?.length || 0;

    // Count outbound messages (these would be the ones we sent)
    const outboundMessages =
      recentMessages?.filter((msg) => msg.from === twilioPhoneNumber) || [];

    // Count errors: check for 'failed' status OR error_code presence
    const errorCount =
      recentMessages?.filter(
        (msg) =>
          msg.status === "failed" ||
          msg.status === "undelivered" ||
          (msg.error_code && msg.error_code !== "None"),
      ).length || 0;

    const successRate =
      totalCount > 0
        ? Math.round(((totalCount - errorCount) / totalCount) * 100)
        : 100;

    return {
      totalCount,
      errorCount,
      successRate,
    };
  } catch (error) {
    console.error("Failed to fetch message statistics:", error);
    return {
      totalCount: 0,
      errorCount: 0,
      successRate: 100,
    };
  }
};

export const getTwilioHealthWithMessages = async (
  page = 0,
  searchQuery = "",
  getAll = false,
) => {
  try {
    // Get basic configuration and sync messages from Twilio
    // Only sync on first page load or if explicitly requested (page 0 usually implies refresh/init)
    // Don't sync when getting all messages (filter mode)
    const shouldSync = page === 0 && !searchQuery && !getAll;
    const config = await getTwilioConfiguration(shouldSync);

    // Get message history and statistics with pagination or all messages
    const limit = getAll ? 10000 : MESSAGES_PER_PAGE; // Use large limit for getAll
    const [messageResult, messageStats] = await Promise.all([
      fetchMessageHistory(page, limit, searchQuery, getAll),
      getMessageStatistics(),
    ]);

    // Get total count for pagination from the message result (which accounts for filtering)
    const totalCount = messageResult.totalCount;
    const totalPages = getAll ? 1 : Math.ceil(totalCount / MESSAGES_PER_PAGE);

    return {
      ...config,
      messageCount: messageStats.totalCount,
      errorCount: messageStats.errorCount,
      successRate: messageStats.successRate,
      recentMessages: messageResult.messages,
      totalMessageCount: totalCount,
      currentPage: page,
      totalPages: totalPages,
      lastCheck: new Date().toISOString(),
      isAllMessages: getAll,
    };
  } catch (error) {
    console.error("Failed to get complete Twilio health data:", error);
    return await getTwilioConfiguration();
  }
};

export default {
  getTwilioConfiguration,
  testTwilioConnection,
  fetchMessageHistory,
  getMessageStatistics,
  getTwilioHealthWithMessages,
  getTotalMessageCount,
  resendTwilioMessage,
  MESSAGES_PER_PAGE,
};

// Fetch patient information by phone number
export const getPatientByPhone = async (phoneNumber) => {
  try {
    const response = await fetch(`${API_BASE_URL}/twilio/find-patient/?phone=${encodeURIComponent(phoneNumber)}`);
    if (!response.ok) {
      if (response.status === 404) {
        return null; // Patient not found
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (data.success && data.patient) {
      return data.patient;
    }
    return null;
  } catch (error) {
    console.error("Error fetching patient by phone:", error);
    return null;
  }
};

// Batch fetch patients for multiple phone numbers
export const getPatientsByPhoneBatch = async (phoneNumbers) => {
  if (!phoneNumbers || phoneNumbers.length === 0) {
    return {};
  }
  
  try {
    const results = {};
    // Process in batches of 10 to avoid overwhelming the server
    const batchSize = 10;
    
    for (let i = 0; i < phoneNumbers.length; i += batchSize) {
      const batch = phoneNumbers.slice(i, i + batchSize);
      
      // Create a Promise.all for this batch
      const batchPromises = batch.map(async (phone) => {
        try {
          const response = await fetch(`${API_BASE_URL}/twilio/find-patient/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phone }),
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('[SMS DEBUG] find-patient endpoint returned:', JSON.stringify(data));
            if (data.success && data.patient) {
              console.log('[SMS DEBUG] Found patient for phone', phone, ':', data.patient.name);
              results[phone] = data.patient;
            } else {
              console.log('[SMS DEBUG] No patient found for phone', phone, '- response:', JSON.stringify(data));
            }
          } else {
            console.log('[SMS DEBUG] find-patient endpoint error for phone', phone, ':', response.status);
          }
        } catch (err) {
          console.warn(`Failed to fetch patient for ${phone}:`, err);
        }
      });
      
      await Promise.all(batchPromises);
    }
    
    return results;
  } catch (error) {
    console.error("Error in batch patient lookup:", error);
    return {};
  }
};

// Get all patients with phone numbers for preloading (with caching)
export const getAllPatients = async (forceRefresh = false) => {
  const now = Date.now();
  
  // Check in-memory cache first
  if (!forceRefresh && patientCacheInMemory && patientCacheTimestamp) {
    if (now - patientCacheTimestamp < PATIENT_CACHE_DURATION) {
      console.log('[SMS DEBUG] getAllPatients: using in-memory cache,', patientCacheInMemory.length, 'patients');
      return patientCacheInMemory;
    }
  }
  
  // Check sessionStorage cache
  if (!forceRefresh) {
    try {
      const cached = sessionStorage.getItem(PATIENT_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (now - timestamp < PATIENT_CACHE_DURATION) {
          console.log('[SMS DEBUG] getAllPatients: using sessionStorage cache,', data.length, 'patients');
          // Also restore to in-memory cache
          patientCacheInMemory = data;
          patientCacheTimestamp = timestamp;
          return data;
        }
      }
    } catch (e) {
      console.warn('[SMS DEBUG] sessionStorage cache read failed:', e);
    }
  }
  
  // Fetch fresh data
  try {
    const response = await fetch(`${API_BASE_URL}/twilio/all-patients/`);
    if (!response.ok) {
      console.error('[SMS DEBUG] getAllPatients error:', response.status);
      return patientCacheInMemory || [];
    }
    const data = await response.json();
    console.log('[SMS DEBUG] getAllPatients returned:', data.count, 'patients');
    
    if (data.success && data.patients) {
      // Store in memory cache
      patientCacheInMemory = data.patients;
      patientCacheTimestamp = now;
      
      // Store in sessionStorage
      try {
        sessionStorage.setItem(PATIENT_CACHE_KEY, JSON.stringify({
          data: data.patients,
          timestamp: now
        }));
      } catch (e) {
        console.warn('[SMS DEBUG] sessionStorage cache write failed:', e);
      }
      
      return data.patients;
    }
    return [];
  } catch (error) {
    console.error("Error fetching all patients:", error);
    return patientCacheInMemory || [];
  }
};

// Clear patient cache (useful when patient data changes)
export const clearPatientCache = () => {
  patientCacheInMemory = null;
  patientCacheTimestamp = null;
  try {
    sessionStorage.removeItem(PATIENT_CACHE_KEY);
  } catch (e) {
    console.warn('[SMS DEBUG] sessionStorage cache clear failed:', e);
  }
};
